import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  User, 
  Lock, 
  Mail, 
  Shield, 
  Eye, 
  EyeOff, 
  Check, 
  AlertTriangle,
  Loader2,
  KeyRound
} from 'lucide-react';

const API = `${API_BASE}/api`;

export default function SettingsPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    checkPasswordStatus();
  }, [token]);

  const checkPasswordStatus = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/password-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMustChangePassword(response.data.must_change_password);
      if (response.data.must_change_password) {
        setActiveTab('security');
        toast.warning('Please change your password to continue');
      }
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        null,
        {
          params: {
            current_password: passwordForm.currentPassword,
            new_password: passwordForm.newPassword
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMustChangePassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Account Settings</h1>
          <p className="text-zinc-400 mt-1">Manage your account preferences and security</p>
        </div>

        {/* Password Change Required Banner */}
        {mustChangePassword && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-amber-400 font-medium">Password Change Required</h3>
              <p className="text-sm text-zinc-400 mt-1">
                For security reasons, you must change your password before continuing.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 p-1 bg-zinc-900 rounded-lg mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={mustChangePassword && tab.id !== 'security'}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-navigate-gold text-black'
                  : mustChangePassword && tab.id !== 'security'
                    ? 'text-zinc-600 cursor-not-allowed'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <User className="h-5 w-5 text-navigate-gold" />
              Profile Information
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-navigate-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-navigate-gold">
                    {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">{user?.full_name || 'User'}</h3>
                  <p className="text-zinc-400">{user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-navigate-gold/20 text-navigate-gold text-xs rounded-full capitalize">
                    {user?.role || 'learner'}
                  </span>
                </div>
              </div>

              <div className="grid gap-4 pt-4 border-t border-zinc-800">
                <div>
                  <Label className="text-zinc-400">Email Address</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4 text-zinc-500" />
                    <span className="text-white">{user?.email}</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Account Type</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Shield className="h-4 w-4 text-zinc-500" />
                    <span className="text-white capitalize">{user?.role || 'Learner'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-navigate-gold" />
              Change Password
            </h2>
            
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              {/* Current Password */}
              <div>
                <Label className="text-zinc-300">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                    className="bg-zinc-800 border-zinc-700 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <Label className="text-zinc-300">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                    className="bg-zinc-800 border-zinc-700 pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Minimum 8 characters</p>
              </div>

              {/* Confirm New Password */}
              <div>
                <Label className="text-zinc-300">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="bg-zinc-800 border-zinc-700 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                )}
                {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
                  <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Passwords match
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !passwordForm.currentPassword || !passwordForm.newPassword || passwordForm.newPassword !== passwordForm.confirmPassword}
                className="w-full bg-navigate-gold hover:bg-navigate-gold/90 text-black mt-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
